import React, { useEffect, useRef, useState } from "react";

type Option = { label: string; value: string | number };

type Props = {
  options: Option[];
  selectedValue?: string | number | null;
  onValueChange: (value: string | number) => void;
  placeholder?: string;
  className?: string;
};

/**
 * Simple web-friendly CustomPicker.
 * - Matches the props used in migrated pages.
 * - Uses a native-like dropdown built with divs so styling is easy with Tailwind.
 * - Handles click-outside to close, keyboard Escape to close.
 */
const CustomPicker: React.FC<Props> = ({ options, selectedValue, onValueChange, placeholder = "Select...", className = "" }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const selectedLabel = (() => {
    const found = options.find((o) => String(o.value) === String(selectedValue));
    return found ? found.label : null;
  })();

  return (
    <div ref={rootRef} className={`relative inline-block w-full ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left bg-white border border-gray-300 rounded px-3 py-2 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-primario-celeste-200"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`block ${selectedLabel ? "text-gray-800" : "text-gray-400"}`}>
          {selectedLabel ?? placeholder}
        </span>
      </button>

      {open && (
        <ul
          role="listbox"
          tabIndex={-1}
          className="absolute z-50 mt-1 w-full max-h-56 overflow-auto bg-white border border-gray-200 rounded shadow-lg"
        >
          {options.length === 0 && (
            <li className="px-3 py-2 text-sm text-gray-500">No hay opciones</li>
          )}
          {options.map((opt) => {
            const isSelected = String(opt.value) === String(selectedValue);
            return (
              <li
                key={String(opt.value)}
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onValueChange(opt.value);
                  setOpen(false);
                }}
                className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${isSelected ? "bg-primario-celeste-50 font-semibold" : ""}`}
              >
                {opt.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default CustomPicker;